// AI Content Generator (editor): writes a script, voice-over, caption or article for a title.
// Uses the same AI settings as AI Studio: Toons AI (credits) or the user's own Claude / OpenAI key,
// chosen here too (AI settings). Before the API has the new endpoint, the old one is used.

import React, { useState, useContext } from "react";
import Button from "@mui/material/Button";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import AuthContext from "../context/userAuth.context";
import { useAIJobs } from "../AIStudio/AIJobsContext";
import AISettings from "../AIStudio/AISettings";
import { aiApi } from "../Utils/aiTemplate/aiApi";
import "@dotlottie/player-component";

/** Which AI writes the text, and what it costs: "Toons AI · 4 credits left" / "Your Claude key ...a9F2". */
function ModeLine({ settings, onSettings }) {
  if (!settings) return null;
  let icon = "flash_on";
  let text;
  if (settings.mode === "own" && settings.hasKey) {
    icon = "vpn_key";
    text = `Your ${settings.provider === "openai" ? "OpenAI" : "Claude"} key …${settings.keyHint} · no credits used`;
  } else if (settings.credits && settings.credits.unlimited) {
    text = "Toons AI · unlimited";
  } else if (settings.credits) {
    const left = settings.credits.remaining;
    text = `Toons AI · ${left} credit${left === 1 ? "" : "s"} left · 1 credit per text`;
  } else {
    text = "Toons AI";
  }
  return (
    <div className="aic-mode">
      <span className="aic-mode-chip">
        <span className="material-icons">{icon}</span>
        {text}
      </span>
      <button className="aic-link" onClick={onSettings}>
        <span className="material-icons">settings</span> AI settings
      </button>
    </div>
  );
}

export default function AIContent(props) {
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 800,
    maxWidth: "calc(100vw - 32px)",
    height: 600,
    maxHeight: "calc(100vh - 32px)",
    overflowY: "auto",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
    resize: "none",
  };

  const { authenticationData } = useContext(AuthContext);
  const { settings, reloadSettings } = useAIJobs();

  const [loading, setloading] = useState(false);
  const [problem, setProblem] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  const closeAIPopup = () => {
    props.setAIContentPopup(false);
  };

  const authHeader = () => {
    try {
      const authData = JSON.parse(localStorage.getItem("animation_studio_2.0_access_token"));
      return authData ? { Authorization: "Bearer " + authData.access_token } : {};
    } catch (e) {
      return {};
    }
  };

  /** The API before AI settings were used here (Toons' OpenAI key, no credits). */
  const generateOld = async (title) => {
    const formdata = new FormData();
    formdata.append("title", title);
    try {
      const response = await axios.post(authenticationData.ApiBaseURL + "/api/Export/getChatGPTResponse", formdata, { headers: authHeader() });
      if (response.data && response.data.status === false) {
        setProblem({ message: response.data.response || "AI content is not available right now. Please try again later." });
        return;
      }
      props.setArticle(response.data.response);
    } catch (e) {
      setProblem({ message: "AI content is not available right now. Please try again later." });
    }
  };

  const GenerateArticle = async () => {
    const title = props.title == null ? "" : String(props.title).trim();
    if (!title) {
      setProblem({ message: "Please enter a title." });
      return;
    }
    setProblem(null);
    setloading(true);
    const res = await aiApi.content(authenticationData.ApiBaseURL, { title });
    if (res.code === "not_deployed") {
      await generateOld(title);
    } else if (res.status) {
      props.setArticle(res.text || "");
      if (res.settings) reloadSettings();
    } else {
      setProblem(res);
    }
    setloading(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !loading) {
      GenerateArticle();
    }
  };

  const CopyContent = () => {
    navigator.clipboard.writeText(props.article);
  };

  const fixInSettings = problem && ["no_credits", "no_key", "toons_unavailable", "invalid_key", "no_credit"].includes(problem.code);

  return (
    <div style={{ borderRadius: 25 }}>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={props.AIContentPopup}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={props.AIContentPopup}>
          <Box sx={style}>
            <Button
              size="small"
              color="error"
              onClick={closeAIPopup}
              style={{
                float: "right",
                height: "25px",
                marginLeft: "100px",
                marginTop: "1px",
              }}
            >
              <CloseIcon
                style={{
                  color: "white",
                  backgroundColor: "#296AFF",
                  borderRadius: "5px",
                }}
              />
            </Button>
            <Paper
              style={{
                borderRadius: 5,
                backgroundColor: "transparent",
                borderStyle: "none",
                border: "none",
                resize: "none",
                boxShadow: "none",
              }}
            >
              <h1>AI Content Generator</h1>
              <ModeLine settings={settings} onSettings={() => setShowSettings(!showSettings)} />
              {showSettings && (
                <div className="aic-settings">
                  <AISettings onClose={() => setShowSettings(false)} />
                </div>
              )}

              <Grid style={{ paddingLeft: "5px" }} container spacing={2}>
                <Grid style={{ paddingTop: "10px", margin: "2px" }} item xs={2} md={12}>
                  <TextField
                    value={props.title}
                    onKeyDown={handleKeyDown}
                    style={{ width: "70%", height: "65px" }}
                    onChange={(e) => props.setTitle(e.target.value)}
                    required
                    id="standard-required"
                    label="Enter Title Here...."
                    variant="standard"
                  />

                  <Button
                    style={{
                      marginTop: "10px",
                      marginLeft: "10px",
                      background: "#296AFF",
                    }}
                    onClick={GenerateArticle}
                    disabled={loading}
                    variant="contained"
                    color="success"
                  >
                    Generate
                  </Button>
                  <Button
                    style={{
                      marginTop: "10px",
                      marginLeft: "10px",
                      background: "#296AFF",
                    }}
                    onClick={CopyContent}
                    variant="contained"
                    color="success"
                  >
                    Copy
                  </Button>
                  {problem && (
                    <div className="aic-problem">
                      <span className="material-icons">info</span>
                      <span>{problem.message}</span>
                      {fixInSettings && (
                        <button className="aic-link" onClick={() => setShowSettings(true)}>
                          Open AI settings
                        </button>
                      )}
                    </div>
                  )}
                </Grid>
                <Grid container spacing={2}>
                  {loading && (
                    <dotlottie-player
                      src="https://assets2.lottiefiles.com/packages/lf20_QdH33DmN0r.json"
                      autoplay
                      loop
                      style={{
                        height: "55%",
                        width: "55%",
                        marginLeft: "160px",
                      }}
                    />
                  )}

                  {!loading && (
                    <Grid
                      style={{
                        border: "none",
                        borderStyle: "none",
                        borderColor: "Transparent",
                      }}
                      item
                      xs={2}
                      md={11.2}
                    >
                      <textarea
                        style={{
                          width: "100%",
                          height: "400px",
                          border: "none",
                          marginLeft: "20px",
                          borderStyle: "none",
                          borderColor: "Transparent",
                          resize: "none",
                        }}
                        onChange={(e) => {
                          props.setArticle(e.target.value);
                        }}
                        value={props.article}
                      ></textarea>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Paper>
          </Box>
        </Fade>
      </Modal>
    </div>
  );
}
